import {Link, Navigate, Outlet, useLocation, useNavigate} from "react-router-dom";
import {useContext, useState} from "react";
import {StorageContext} from "../storage/StorageContext";
import {BiHeart, BiMenu} from "react-icons/bi";
import {Avatar, Dropdown} from "flowbite-react";
import { useApp } from "../hooks/appHook";
import { getIonIcon } from "../commons/IonIconList";

const PrivateLayout = () => {
    const { storage } = useContext(StorageContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [app] = useApp();
    const [showWarning, setShowWarning] = useState(false);

    const links = [
        { path: '/account', label: 'Accueil', icon: 'podium-outline', hidden: !storage.company?.can_org_votes },
        { path: '/account/sessions', label: 'Sessions', scope: 'all', icon: 'calendar-number-outline', hidden: !storage.company?.can_org_votes },
        { path: '/account/sessions/steps', label: 'Étapes', scope: 'all', icon: 'footsteps-outline', hidden: true },
        { path: '/account/sessions/candidates', label: 'Candidats', scope: 'all', icon: 'body-outline', hidden: !storage.company?.can_org_votes },
        { path: '/account/sessions/votes', label: 'Votes', scope: 'all', icon: 'star-outline', hidden: !storage.company?.can_org_votes },
        { path: '/account/tickets', label: 'Billeterie', scope: 'all', icon: 'ticket-outline', iconSize: 16, hidden: !storage.company?.can_sell_ticket },
        { path: '/account/withdraw', label: 'Retraits', icon: 'cash-outline' },
        { path: '/account/settings', label: 'Paramètres', icon: 'cog-outline', iconSize: 20, hidden: true },
    ];

    const path = location.pathname;
    const canVotes = !!storage.company?.can_org_votes;
    const canTickets = !!storage.company?.can_sell_ticket;
    const votesArea = path === '/account' || path.startsWith('/account/sessions');
    const ticketsArea = path.startsWith('/account/tickets');

    if (storage.user && votesArea && !canVotes) {
        return <Navigate to={canTickets ? '/account/tickets' : '/account/withdraw'} replace />;
    }
    if (storage.user && ticketsArea && !canTickets) {
        return <Navigate to={canVotes ? '/account' : '/account/withdraw'} replace />;
    }

    return storage.user ? (
        // bg-[#F9FAFB]
        <div className={`h-screen w-full overflow-auto flex flex-col pt-[69px]`}>
            <header className={`bg-gray-900 fixed w-full top-0 left-0 z-10`}>
                <div className={`max-w-screen-2xl mx-auto px-5 py-[17px] flex items-center`}>
                    {/* brand */}
                    <div className={`shrink-0`}>
                        <img src="/assets/images/logo-nobg.png" className={`w-24 rounded-md`} alt=""/>
                    </div>

                    {/* menu */}
                    <ul className={`m-0 p-0 ml-16 gap-7 hidden lg:flex`}>
                        {links
                            .filter(x => (!x.scope || x.scope === 'all' || x.scope === app) && !x.hidden)
                            .map((item, idx) => {
                                const Icon = getIonIcon(item.icon);
                                return (
                                    <li key={idx + 1}>
                                        <Link
                                            to={item.path}
                                            className={`
                                                text-white text-[14.7px] font-medium flex items-center
                                                ${location.pathname === item.path || (item.path !== '/account' && location.pathname.startsWith(item.path + '')) ? '!text-orange-500 border-l-2 border-orange-500 pl-2' : ''}
                                            `}
                                        >
                                            {Icon ? <Icon size={item.iconSize || 17} className={`mr-2`} /> : null}
                                            {item.label}
                                        </Link>
                                    </li>
                                )
                            })
                        }
                    </ul>

                    {/* aside */}
                    <div className={`ml-auto flex items-center shrink-0`}>
                        <h4 className={`text-sm text-white mr-3 font-bold`}>{storage.user.name.split(' ')[0]}</h4>
                        <div className={``}>
                            <Dropdown
                                inline
                                className={`w-[200px]`}
                                renderTrigger={() => <Avatar size="md" alt="User settings" img="/assets/images/avatar.png" rounded/>}
                                label=""
                            >
                                <Dropdown.Header>
                                    <span className="block text-sm font-semibold">{storage.user.name}</span>
                                    <span className="block truncate text-sm text-orange-400 font-medium">
                                      {storage.user.email || ''}
                                    </span>
                                </Dropdown.Header>
                                <Dropdown.Item onClick={() => navigate('/account/settings/profile')}>Profil</Dropdown.Item>
                                <Dropdown.Item onClick={() => navigate('/account/settings/company')}>Paramètres</Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => navigate('/logout')}>Déconnexion</Dropdown.Item>
                            </Dropdown>
                        </div>
                        <div className={`block lg:hidden ml-4`}>
                            <Dropdown
                                inline
                                className={`w-[200px]`}
                                renderTrigger={() => <BiMenu size={25} color="#fff" />}
                                label=""
                            >
                                {links.filter(x => !x.hidden).map((item, idx) => (
                                    <Dropdown.Item key={idx + 1} className={`hover:text-orange-500`}>
                                        <Link to={item.path}>{item.label}</Link>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </header>
            {showWarning ? (
                <section className={`max-w-screen-2xl mx-auto px-7 w-full bg-red-600 relative`}>
                    <button
                        className={`w-7 h-7 rounded-full bg-white text-red-600 text-2xl absolute top-4 right-4`}
                        onClick={() => setShowWarning(false)}
                    >&times;</button>
                    <div className={`w-full p-7 max-w-3xl mx-auto text-center`}>
                        <h1 className={`text-4xl font-bold text-white`}>DERNIER RAPPEL !!!</h1>
                        <p className={`text-xl text-red-50`}>
                            Pour garder une securité maximale sur Chooz, nous ajouterons l'authentification
                            à double facteurs pour toute tentative de connexion et retraits. Cette mesure entrera en vigueur
                            <b className={`uppercase text-white font-extrabold`}> dès le 26 septembre 2025.</b> <br/><br/>
                            Nous vous invitons à envoyer par e-mail dans les plus brefs delais <b>l'adresse email actuelle, la nouvelle adresse email et le numéro de téléphone</b> corrects à associer à votre compte. <br/><br/>
                            Nous écrire à: <a href="mailto:contact@chooz-app.com" className={`text-white font-bold underline`}>support@chooz-app.com</a> <br/><br/>
                            Sujet: <b>Nouvelle authentification</b>
                        </p>
                    </div>
                </section>
            ) : null}
            <div className={`max-w-screen-2xl mx-auto px-5 pb-4 w-full`}>
                <Outlet />
            </div>
            <footer className={`w-full mt-auto bg-gray-100 py-3`}>
                <div className={`max-w-screen-2xl px-5 mx-auto`}>
                    <p className={`text-xs text-center text-gray-400 font-semibold`}>
                        With <BiHeart className={`text-orange-500 inline-block`} />
                        &nbsp;by Sarrux
                    </p>
                </div>
            </footer>
        </div>
    ) : (
        <Navigate to="/auth/login" />
    );
};

export default PrivateLayout;
